/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListAIDetectionUsersRequest,
  ListAIDetectionUsersSecurity,
} from "../models/operations/listaidetectionusers.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AiDetectionUsersQueryData,
  buildAiDetectionUsersQuery,
  prefetchAiDetectionUsers,
  queryKeyAiDetectionUsers,
} from "./aiDetectionUsers.core.js";
export {
  type AiDetectionUsersQueryData,
  buildAiDetectionUsersQuery,
  prefetchAiDetectionUsers,
  queryKeyAiDetectionUsers,
};

export type AiDetectionUsersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listAIDetectionUsers access
 *
 * @remarks
 * List the enrolled users one detected AI tool was found for, each with their devices, signals, versions and first and last sightings: the evidence listEmployeeAIDetections gives per tool for one person, expanded the other way round. Org-scoped like listAIDetections and, like it, requires an authenticated session authorized for org:admin on the active organization. Linked alias emails are folded to the canonical identity, so one person is one row. A target with no detections in the organization is not_found.
 */
export function useAiDetectionUsers(
  request: ListAIDetectionUsersRequest,
  security?: ListAIDetectionUsersSecurity | undefined,
  options?: QueryHookOptions<
    AiDetectionUsersQueryData,
    AiDetectionUsersQueryError
  >,
): UseQueryResult<AiDetectionUsersQueryData, AiDetectionUsersQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildAiDetectionUsersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listAIDetectionUsers access
 *
 * @remarks
 * List the enrolled users one detected AI tool was found for, each with their devices, signals, versions and first and last sightings: the evidence listEmployeeAIDetections gives per tool for one person, expanded the other way round. Org-scoped like listAIDetections and, like it, requires an authenticated session authorized for org:admin on the active organization. Linked alias emails are folded to the canonical identity, so one person is one row. A target with no detections in the organization is not_found.
 */
export function useAiDetectionUsersSuspense(
  request: ListAIDetectionUsersRequest,
  security?: ListAIDetectionUsersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    AiDetectionUsersQueryData,
    AiDetectionUsersQueryError
  >,
): UseSuspenseQueryResult<
  AiDetectionUsersQueryData,
  AiDetectionUsersQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAiDetectionUsersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setAiDetectionUsersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: { targetId: string; gramSession?: string | undefined },
  ],
  data: AiDetectionUsersQueryData,
): AiDetectionUsersQueryData | undefined {
  const key = queryKeyAiDetectionUsers(...queryKeyBase);

  return client.setQueryData<AiDetectionUsersQueryData>(key, data);
}

export function invalidateAiDetectionUsers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { targetId: string; gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "access",
      "listAIDetectionUsers",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllAiDetectionUsers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listAIDetectionUsers"],
  });
}
